<template>
  <div class="PlayerSelectList">
    <ul class="PlayerSelectList__list">
      <label
        v-for="player in players"
        :key="player.id"
        :id="`playerSelectListLabel-${player.id}`"
        class="PlayerSelectList__item"
        :data-testid="player.id"
      >
        <input
          v-model="selectedPlayerId"
          class="PlayerSelectList__itemInput"
          type="radio"
          :value="player.id"
          :checked="selectedPlayerId === player.id"
          :aria-labelledby="`playerSelectListLabel-${player.id}`"
          :required="isRequired"
          @input="haptic()"
        >
        <PlayerAvatar
          class="PlayerSelectList__itemAvatar"
          :modelValue="player"
        />
        <span class="PlayerSelectList__itemLabel">
          {{ player.name }}
        </span>
        <Transition>
          <Icon
            v-if="selectedPlayerId === player.id"
            class="PlayerSelectList__itemIcon"
            name="trophyWithStar"
            size="large"
          />
        </Transition>
      </label>
    </ul>
  </div>
</template>

<script lang="ts" setup>
import haptic from "@/lib/utils/platform/haptic";
import { PlayerSummary } from "@shared/lib/database/query.types";
import Icon from "./Icon.vue";
import PlayerAvatar from "./PlayerAvatar.vue";

const selectedPlayerId = defineModel<string | null>();

const {
  players,
  isRequired = false,
} = defineProps<{
  players: Array<PlayerSummary>,
  isRequired?: boolean,
}>();
</script>

<style lang="scss" scoped>
.PlayerSelectList {
  display: grid;
  min-height: 0;

  border: solid 2px var(--themeColorNeutral900);
  border-radius: 8px;
  background-color: var(--themeColorWhite);

  transition: 350ms;
  transition-property: border-color, background-color, color;

  &:has(:disabled) {
    border-color: var(--themeDarkTransparent500);
    background-color: var(--themeLightTransparent600);
    color: var(--themeDarkTransparent500);
  }
}

.PlayerSelectList__list {
  position: relative;

  display: grid;
  gap: 1px;
  overflow-y: auto;

  border-radius: 6px;
}

.PlayerSelectList__item {
  --transitionDuration: 250ms;

  position: relative;

  display: flex;
  align-items: center;
  padding: 8px 16px 8px 8px;

  outline: solid 1px var(--themeColorNeutral100);
  background-color: var(--themeColorWhite);

  font-family: var(--fontFamilyMono);
  font-weight: var(--fontWeightMedium);

  cursor: pointer;

  transition: var(--transitionDuration);
  transition-property: background-color, outline-color, color;

  &:first-of-type {
    border-radius: 6px 6px 0 0;
  }

  &:last-of-type {
    border-radius: 0 0 6px 6px;
  }

  &:has(:checked) {
    outline-color: var(--colorGold);
    background-color: var(--colorGold);
  }

  &:has(:checked) + &,
  &:has(+ & :checked) {
    outline-color: transparent;
  }

  &:has(:checked):has(:disabled) {
    outline-color: var(--themeColorNeutral150);
    background-color: var(--themeColorNeutral150);
  }

  &::after {
    position: absolute;
    inset: 0;

    border-radius: inherit;
    box-shadow: 0 0 0 0 transparent inset;

    pointer-events: none;

    transition: box-shadow var(--transitionDuration);

    content: "";
  }

  &:has(input:focus-visible)::after {
    box-shadow: 0 0 0 3px var(--themeColorSecondary) inset;
  }
}

.PlayerSelectList__itemInput {
  position: absolute;
  top: 0;
  left: 0;

  opacity: 0;
  appearance: none;
}

.PlayerSelectList__itemAvatar {
  flex-shrink: 0;
  margin-right: 12px;

  transition: 350ms;
  transition-property: filter, opacity;

  .PlayerSelectList__item:has(:disabled) & {
    opacity: 0.5;
    filter: grayscale(100%);
  }
}

.PlayerSelectList__itemLabel {
  display: grid;
  flex: 1 1 auto;
  align-items: center;
  min-height: 48px;
  padding: 4px 0;
  overflow: hidden;

  font-size: 20px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.PlayerSelectList__itemIcon {
  transition: var(--transitionDuration) opacity;

  &.v-enter-from,
  &.v-leave-to {
    opacity: 0;
  }
}
</style>
